import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Card, CardBody, Button } from "reactstrap";
import Loader from "../../Components/Common/Loader";

const HEATMAP_STOPS = [
  { t: 0, rgb: [229, 107, 79] },
  { t: 0.18, rgb: [229, 107, 79] },
  { t: 0.42, rgb: [229, 168, 59] },
  { t: 0.68, rgb: [185, 207, 43] },
  { t: 1, rgb: [32, 149, 72] },
];

const lerp = (start, end, amount) => start + (end - start) * amount;

const valueToHeatColor = (ratio) => {
  if (!Number.isFinite(ratio)) return "#e9ecef";
  const t = Math.max(0, Math.min(1, ratio));
  let start = HEATMAP_STOPS[0];
  let end = HEATMAP_STOPS[HEATMAP_STOPS.length - 1];
  for (let index = 0; index < HEATMAP_STOPS.length - 1; index += 1) {
    if (t >= HEATMAP_STOPS[index].t && t <= HEATMAP_STOPS[index + 1].t) {
      start = HEATMAP_STOPS[index];
      end = HEATMAP_STOPS[index + 1];
      break;
    }
  }
  const span = end.t - start.t || 1;
  const localT = (t - start.t) / span;
  const red = Math.round(lerp(start.rgb[0], end.rgb[0], localT));
  const green = Math.round(lerp(start.rgb[1], end.rgb[1], localT));
  const blue = Math.round(lerp(start.rgb[2], end.rgb[2], localT));
  return `rgb(${red}, ${green}, ${blue})`;
};

const getCellValue = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0 ? amount : 0;
};

const normalizeRatio = (value, max) => {
  const amount = getCellValue(value);
  if (amount <= 0 || !Number.isFinite(max) || max <= 0) return 0;
  return Math.max(0, Math.min(1, amount / max));
};

const BusinessMixHeatmap = ({
  data = [],
  metric = "Room_should",
  scale = "daily",
  view = "countries",
  loader = false,
  currency = "$",
}) => {
  const [showMore, setShowMore] = useState(false);
  const [tooltip, setTooltip] = useState(null);
  const tooltipRef = useRef(null);

  const rows = useMemo(() => {
    if (!Array.isArray(data)) return [];

    const groupedRows = new Map();
    data.forEach((item) => {
      const name = item?.entity_name || item?.name;
      if (!name) return;

      const key = item.entity_code || name;
      const row = groupedRows.get(key) || {
        name,
        out_of: item.out_of,
        type: view === "segments" || view === "segment" ? "segment" : "country",
        data: [],
      };
      const dateItems = Array.isArray(item.date)
        ? item.date
        : [
            {
              row_dt: item.date || item.business_date || item.day || "Result",
              matrix_value: item.matrix_value,
              rank: item.rank,
            },
          ];
      dateItems.forEach((dateItem) => {
        row.data.push({
          date: dateItem.row_dt,
          matrixValue: dateItem.matrix_value,
          rank_the_day: dateItem.rank ?? item.rank,
        });
      });
      groupedRows.set(key, row);
    });

    return groupedRows.size ? Array.from(groupedRows.values()) : data;
  }, [data, view]);

  const viewType = useMemo(
    () => (view === "segments" || view === "segment" ? "Segment" : "Country"),
    [view],
  );
  const isRevenue = metric === "F&B_Revenue";
  const metricLabel = isRevenue ? "F&B Revenue" : "Rooms Sold";

  const getMetricValue = (item) => {
    if (!item) return 0;
    const value = Number(item.matrixValue);
    return Number.isFinite(value) ? value : 0;
  };

  const dates = useMemo(() => {
    const dateSet = new Set();
    rows.forEach((row) => {
      if (!Array.isArray(row?.data)) return;
      row.data.forEach((item) => {
        if (item?.date) dateSet.add(item.date);
      });
    });
    return Array.from(dateSet).sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime(),
    );
  }, [rows]);

  const normalizedRows = useMemo(() => {
    return rows.map((row) => {
      const dateMap = {};
      if (Array.isArray(row?.data)) {
        row.data.forEach((item) => {
          if (!item?.date) return;
          dateMap[item.date] = { value: getMetricValue(item) };
        });
      }
      return { ...row, dateMap };
    });
  }, [rows]);

  const overallTotals = useMemo(() => {
    return normalizedRows.map((row) => {
      const total = dates.reduce(
        (sum, date) => sum + (row.dateMap[date]?.value || 0),
        0,
      );
      return { ...row, overallTotal: total };
    });
  }, [normalizedRows, dates]);

  const sortedRows = useMemo(
    () => [...overallTotals].sort((a, b) => b.overallTotal - a.overallTotal),
    [overallTotals],
  );
  const visibleRows = showMore ? sortedRows : sortedRows.slice(0, 10);
  const hasMoreRows =
    rows.length > 10 || rows.some((row) => Number(row.out_of) > rows.length);

  const dailyRanks = useMemo(() => {
    const result = {};
    dates.forEach((date) => {
      const sorted = [...visibleRows].sort(
        (a, b) => (b.dateMap[date]?.value || 0) - (a.dateMap[date]?.value || 0),
      );
      result[date] = {};
      sorted.forEach((row, index) => {
        result[date][row.name] = `#${index + 1}`;
      });
    });
    return result;
  }, [dates, visibleRows]);

  const dailyMaxByDate = useMemo(() => {
    const ranges = {};
    dates.forEach((date) => {
      const values = overallTotals.map((row) => getCellValue(row.dateMap[date]?.value));
      ranges[date] = values.length ? Math.max(...values) : 0;
    });
    return ranges;
  }, [dates, overallTotals]);

  const overallMax = useMemo(() => {
    const values = overallTotals.flatMap((row) =>
      dates.map((date) => getCellValue(row.dateMap[date]?.value)),
    );
    return values.length ? Math.max(...values) : 0;
  }, [overallTotals, dates]);

  const monthSpans = useMemo(() => {
    const spans = [];
    dates.forEach((dateValue) => {
      const date = new Date(dateValue);
      if (Number.isNaN(date.getTime())) {
        spans.push({ key: dateValue, label: "", count: 1 });
        return;
      }
      const key = `${date.getFullYear()}-${date.getMonth()}`;
      const label = date.toLocaleDateString("en-US", { month: "short" }).toUpperCase();
      const last = spans[spans.length - 1];
      if (last && last.key === key) {
        last.count += 1;
        return;
      }
      spans.push({ key, label, count: 1 });
    });
    return spans;
  }, [dates]);

  const dateLabelIndexes = useMemo(() => {
    if (dates.length <= 7) {
      return new Set(dates.map((_, index) => index));
    }
    const labelCount = dates.length <= 31 ? 8 : 10;
    const step = (dates.length - 1) / (labelCount - 1);
    return new Set(
      Array.from({ length: labelCount }, (_, index) =>
        Math.round(index * step),
      ),
    );
  }, [dates]);

  const formatValue = (value) => {
    const number = Number(value) || 0;
    return isRevenue
      ? `${currency} ${number.toLocaleString("en-US")}`
      : number.toLocaleString("en-US");
  };

  const formatDate = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const month = date.toLocaleDateString("en-US", { month: "short" });
    return `${date.getDate()} - ${month.toLowerCase()}`;
  };

  const formatDayLabel = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return String(date.getDate());
  };

  const gridColumns = {
    display: "grid",
    gridTemplateColumns: `repeat(${dates.length}, minmax(10px, 1fr))`,
    columnGap: "3px",
  };

  const getCellColor = (row, date) => {
    const rawValue = getCellValue(row.dateMap[date]?.value);
    const max =
      scale === "overall" ? overallMax : dailyMaxByDate[date] || 0;
    return valueToHeatColor(normalizeRatio(rawValue, max));
  };

  const showTooltip = (event, row, date) => {
    const cell = event.currentTarget.getBoundingClientRect();
    const rawValue = row.dateMap[date]?.value || 0;
    setTooltip({
      cellTop: cell.top,
      cellBottom: cell.bottom,
      cellLeft: cell.left,
      cellWidth: cell.width,
      name: row.name,
      date: formatDate(date),
      value: formatValue(rawValue),
      rank: dailyRanks[date]?.[row.name] || "-",
    });
  };

  useLayoutEffect(() => {
    if (!tooltip || !tooltipRef.current) return;
    const el = tooltipRef.current;
    const tooltipWidth = el.offsetWidth;
    const tooltipHeight = el.offsetHeight;
    const padding = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let left = tooltip.cellLeft + tooltip.cellWidth / 2 - tooltipWidth / 2;
    left = Math.max(
      padding,
      Math.min(left, viewportWidth - tooltipWidth - padding),
    );

    const spaceAbove = tooltip.cellTop;
    const spaceBelow = viewportHeight - tooltip.cellBottom;
    let top;
    if (spaceAbove >= tooltipHeight + padding || spaceAbove > spaceBelow) {
      top = tooltip.cellTop - tooltipHeight - padding;
      if (top < padding) {
        top = tooltip.cellBottom + padding;
      }
    } else {
      top = tooltip.cellBottom + padding;
      if (top + tooltipHeight > viewportHeight - padding) {
        top = Math.max(padding, viewportHeight - tooltipHeight - padding);
      }
    }

    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
    el.style.visibility = "visible";
  }, [tooltip]);

  if (!rows.length) {
    return (
      <Card className="border-0 shadow-sm">
        <CardBody
          className="position-relative d-flex align-items-center justify-content-center"
          style={{ minHeight: "300px" }}
        >
          {loader ? (
            <Loader />
          ) : (
            <div className="text-center text-muted">No data available</div>
          )}
        </CardBody>
      </Card>
    );
  }

  const rowHeight = 26;
  const rowDivider = "1px solid #ececec";

  return (
    <Card className="border-0 shadow-sm rounded-3 overflow-hidden w-100">
      <CardBody className="p-0 w-100">
        <div className="d-flex w-100 overflow-hidden">
          <div
            className="flex-shrink-0 bg-white"
            style={{ flex: "0 0 160px", zIndex: 2 }}
          >
            <div style={{ height: "36px" }} />
            <div
              className="d-flex align-items-center text-muted px-2"
              style={{
                height: "22px",
                fontSize: "13px",
                fontWeight: 600,
                borderBottom: rowDivider,
              }}
            >
              {viewType}
            </div>
            {visibleRows.map((row) => (
              <div
                key={row.name}
                className="d-flex align-items-center text-dark px-2 text-truncate"
                title={row.name}
                style={{
                  height: `${rowHeight}px`,
                  fontSize: "13px",
                  fontWeight: 400,
                  lineHeight: 1.2,
                  borderBottom: rowDivider,
                }}
              >
                {row.name}
              </div>
            ))}
          </div>

          <div
            className="flex-grow-1 w-100 overflow-hidden position-relative px-2 pb-2"
            style={{ minWidth: 0, flexBasis: 0 }}
          >
            <div style={{ ...gridColumns, height: "36px" }}>
              {monthSpans.map((span) => (
                <div
                  key={span.key}
                  className="d-flex align-items-end text-muted"
                  style={{
                    gridColumn: `span ${span.count}`,
                    fontSize: "10px",
                    fontWeight: 600,
                    letterSpacing: "0.04em",
                    paddingBottom: "2px",
                  }}
                >
                  {span.label}
                </div>
              ))}
            </div>
            <div
              style={{
                ...gridColumns,
                height: "22px",
                borderBottom: rowDivider,
              }}
            >
              {dates.map((date, index) => (
                <div
                  key={date}
                  className="d-flex align-items-center justify-content-center text-muted"
                  style={{ fontSize: "9px", fontWeight: 500 }}
                >
                  {dateLabelIndexes.has(index) ? formatDayLabel(date) : ""}
                </div>
              ))}
            </div>
            {visibleRows.map((row) => (
              <div
                key={row.name}
                style={{
                  ...gridColumns,
                  height: `${rowHeight}px`,
                  borderBottom: rowDivider,
                }}
              >
                {dates.map((date) => (
                  <div
                    key={`${row.name}-${date}`}
                    className="d-flex align-items-center"
                  >
                    <div
                      onMouseEnter={(event) => showTooltip(event, row, date)}
                      onMouseLeave={() => setTooltip(null)}
                      style={{
                        width: "100%",
                        height: "20px",
                        borderRadius: "3px",
                        backgroundColor: getCellColor(row, date),
                        cursor: "pointer",
                        alignSelf: "center",
                      }}
                    />
                  </div>
                ))}
              </div>
            ))}

            {loader && (
              <div
                className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75"
                style={{ zIndex: 10 }}
              >
                <Loader />
              </div>
            )}
          </div>
        </div>

        {hasMoreRows && (
          <div className="px-3 pb-3 pt-2">
            <Button
              type="button"
              color="light"
              size="sm"
              className="border rounded-2 px-3"
              onClick={() => setShowMore((previous) => !previous)}
            >
              {showMore ? "Show Top 10" : "Show More"}
            </Button>
          </div>
        )}
      </CardBody>

      {tooltip && (
        <div
          ref={tooltipRef}
          className="bg-white border rounded shadow-sm p-2"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: 1080,
            pointerEvents: "none",
            minWidth: "160px",
            maxWidth: "240px",
            visibility: "hidden",
          }}
        >
          <div className="small text-dark mb-1">
            {viewType}: <strong>{tooltip.name}</strong>
          </div>
          <div className="small text-dark mb-1">
            Date: <strong>{tooltip.date}</strong>
          </div>
          <div className="small text-dark mb-1">
            {metricLabel}: <strong>{tooltip.value}</strong>
          </div>
          <div className="small text-dark">
            Rank that day: <strong>{tooltip.rank}</strong>
          </div>
        </div>
      )}
    </Card>
  );
};

export default BusinessMixHeatmap;
